<script setup lang="ts" generic="TValue extends Record<string, unknown> = Record<string, unknown>">
import { ref, computed, watch, watchEffect, useTemplateRef, onBeforeUpdate } from 'vue'
import { useI18n } from 'vue-i18n'
import { useResizeObserver } from '@vueuse/core'
import type { SubmitEventPromise } from 'vuetify'
import type { StepConfig, StepperSlotProps, ObservedFStepperStepExpose } from './types'
import mdiArrowLeft from '~icons/mdi/arrow-left'
import mdiCheck from '~icons/mdi/check'

const props = withDefaults(
  defineProps<{
    disabled?: boolean
    disablePreviousStepsAfterSteps?: number[]
  }>(),
  { disabled: false, disablePreviousStepsAfterSteps: () => [] }
)

const model = defineModel<TValue>({ required: true })
const steps = defineModel<StepConfig[]>('steps', { required: true })
const currentStepNumber = defineModel<number>('currentStepNumber', { default: 1 })

const { t } = useI18n()

const completedSteps = ref(new Array(steps.value.length).fill(false))
const disabledSteps = ref(new Array(steps.value.length).fill(false))
const stepShownFlags = ref(new Array(steps.value.length).fill(false))

const stepRefs = ref(new Map<number, ObservedFStepperStepExpose>())

function setStepRef(index: number, el: unknown) {
  if (el == null) {
    stepRefs.value.delete(index)
  } else {
    stepRefs.value.set(index, el as ObservedFStepperStepExpose)
  }
}

onBeforeUpdate(() => {
  stepRefs.value.clear()
})

const stepperWindowRef = useTemplateRef('stepper-window')
const currentStepHeight = ref(0)

const currentStepIndex = computed(() => currentStepNumber.value - 1)
const currentStep = computed(() => steps.value[currentStepIndex.value])
const isFirstStep = computed(() => currentStepNumber.value === 1)
const previousStepDisabled = computed(() => {
  if (isFirstStep.value) return true
  return disabledSteps.value[currentStepIndex.value - 1] === true
})

const slotProps = computed<StepperSlotProps>(() => ({
  currentStepNumber: currentStepNumber.value,
  setCurrentStepNumber,
  currentStepHeight: currentStepHeight.value,
}))

function previousStepsCompleted(index: number): boolean {
  if (index === 0) return true
  for (let i = index - 1; i >= 0; i--) {
    if (!completedSteps.value[i]) return false
  }
  return true
}

function canGoToStep(index: number): boolean {
  const stepNumber = index + 1
  const isCompleted = completedSteps.value[index] === true
  const isDisabled = disabledSteps.value[index] === true || props.disabled
  return isCompleted && !isDisabled && previousStepsCompleted(index) && currentStepNumber.value !== stepNumber
}

async function handleFormSubmit(event: SubmitEventPromise): Promise<void> {
  if (!(await event).valid) return
  await next()
}

async function next(): Promise<void> {
  if (props.disabled) return
  // Same gate as the next button — without it, Enter in a field would submit the step form and
  // bypass the button's disabled state.
  if (completedSteps.value[currentStepIndex.value] !== true) return
  const step = currentStep.value
  if (step?.next == null) return
  let targetStepNumber = currentStepNumber.value + 1
  if (step.next.callback != null) {
    const result = await step.next.callback({ props: step.props ?? {}, stepNumber: currentStepNumber.value })
    if (typeof result === 'number') targetStepNumber = result
    else if (result === null) return
  }
  if (targetStepNumber > steps.value.length) return
  setCurrentStepNumber(targetStepNumber)
}

async function previous(): Promise<void> {
  if (props.disabled) return
  const step = currentStep.value
  if (step?.back == null) return
  let targetStepNumber = currentStepNumber.value - 1
  if (step.back.callback != null) {
    const result = await step.back.callback({ props: step.props ?? {}, stepNumber: currentStepNumber.value })
    if (typeof result === 'number') targetStepNumber = result
    else if (result === null) return
  }
  if (targetStepNumber < 1) return
  setCurrentStepNumber(targetStepNumber)
}

function setCurrentStepNumber(stepNumber: number): void {
  if (stepNumber < 1 || stepNumber > steps.value.length) return
  currentStepNumber.value = stepNumber
}

watch(
  currentStepNumber,
  (newStep) => {
    stepShownFlags.value[newStep - 1] = true
    if (props.disablePreviousStepsAfterSteps.includes(newStep)) {
      for (let i = 0; i < newStep - 1; i++) disabledSteps.value[i] = true
    }
  },
  { immediate: true }
)

watchEffect(() => {
  for (const [index, stepRef] of stepRefs.value.entries()) {
    completedSteps.value[index] = stepRef.stepCompleted
    if (stepRef.stepDisabled != null) {
      disabledSteps.value[index] = stepRef.stepDisabled
    }
  }
})

useResizeObserver(
  () => stepperWindowRef.value?.$el as HTMLElement | undefined,
  (entries) => {
    const entry = entries[0]
    if (entry != null) currentStepHeight.value = entry.contentRect.height
  }
)

defineExpose({ next, previous, setCurrentStepNumber, currentStepNumber, completedSteps, disabledSteps })
</script>

<template>
  <v-stepper
    v-model="currentStepNumber"
    class="f-stepper d-flex flex-column"
    :disabled="disabled"
    flat
    hide-actions
  >
    <v-stepper-header class="f-stepper-header">
      <template
        v-for="(step, index) in steps"
        :key="`header-${index}`"
      >
        <v-stepper-item
          :class="[
            'f-stepper-item',
            {
              'f-stepper-item--inactive': !completedSteps[index] && currentStepNumber !== index + 1,
            },
          ]"
          :value="index + 1"
          :title="t(step.title)"
          :complete="completedSteps[index]"
          :disabled="disabledSteps[index]"
          :editable="canGoToStep(index)"
          :rules="[]"
          :color="completedSteps[index] || currentStepNumber === index + 1 ? 'primary' : undefined"
        >
          <template #icon>
            <v-icon
              v-if="completedSteps[index]"
              :icon="mdiCheck"
              size="small"
            />
            <span v-else>{{ index + 1 }}</span>
          </template>
        </v-stepper-item>
        <v-divider
          v-if="index < steps.length - 1"
          :key="`divider-${index}`"
          class="f-stepper-divider"
        />
      </template>
    </v-stepper-header>

    <v-row>
      <slot
        name="prepend"
        v-bind="slotProps"
      />

      <v-col>
        <v-stepper-window
          ref="stepper-window"
          class="ma-0 bg-background"
        >
          <v-card
            variant="outlined"
            elevation="0"
            class="pa-0"
          >
            <v-stepper-window-item
              v-for="(step, index) in steps"
              :key="`content-${index}`"
              :value="index + 1"
              eager
            >
              <v-form @submit.prevent="handleFormSubmit">
                <div class="pa-6">
                  <v-btn
                    v-if="step.back != null && !previousStepDisabled"
                    type="button"
                    variant="text"
                    :disabled="disabled"
                    :aria-label="t(step.back.label ?? 'common.back')"
                    @click="previous"
                  >
                    <v-icon
                      :icon="mdiArrowLeft"
                      start
                    />
                    {{ t(step.back.label ?? 'common.back') }}
                  </v-btn>

                  <div class="py-3">
                    <component
                      :is="step.component"
                      :ref="(el: unknown) => setStepRef(index, el)"
                      v-model="model"
                      :step-showing="currentStepNumber === index + 1"
                      :step-shown="stepShownFlags[index]"
                      :step-props="step.props ?? {}"
                    />
                  </div>

                  <div class="d-flex justify-end">
                    <slot
                      name="prepend_next_button"
                      v-bind="slotProps"
                    />

                    <v-btn
                      v-if="step.next != null"
                      type="submit"
                      :color="step.next.color ?? 'primary'"
                      :disabled="disabled || completedSteps[index] !== true"
                      variant="flat"
                    >
                      {{ t(step.next.label ?? 'common.next') }}
                    </v-btn>
                  </div>
                </div>
              </v-form>
            </v-stepper-window-item>
          </v-card>
        </v-stepper-window>
      </v-col>

      <slot
        name="append"
        v-bind="slotProps"
      />
    </v-row>
  </v-stepper>
</template>

<style scoped>
.f-stepper {
  background: transparent;
}

.f-stepper-header {
  box-shadow: none;
  height: auto;
  align-items: center;
}

.f-stepper-item {
  flex: 0 0 auto;
  padding: 24px 8px;
}

.f-stepper-header > *:last-child {
  flex-grow: 0;
}

.f-stepper-item--inactive {
  opacity: 0.5;
}

.f-stepper-divider {
  flex: 1 1 auto;
  margin: 0;
  align-self: center;
}

:deep(.v-stepper-item .v-avatar) {
  width: 24px;
  height: 24px;
  font-size: 0.75rem;
  margin-right: 0;
}

:deep(.v-stepper-item) {
  flex-direction: row;
  align-items: center;
  gap: 8px;
}

:deep(.v-stepper-item__content) {
  text-align: left;
}

@media (max-width: 600px) {
  :deep(.v-stepper-item__content) {
    display: none;
  }

  .f-stepper-item {
    padding: 24px 0;
  }

  .f-stepper-divider {
    margin: 0 4px;
  }
}
</style>
